import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import DoneIcon from '@material-ui/icons/Done';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { RecordType } from './ReviewerCommentForm1';
import ReferenceDropdown from './ReferenceDropdown';
import { getReferenceCodes } from '../../api/referenceCodes';
import { DEFAULT_PARAMS_ALL } from '../../constants';

type Props = {
  record: RecordType;
  onChange: Function;
  onCompoundChange: Function;
  saveAll: any;
  statuses: any[];
  showSave: boolean;
  initialValues: any;
};

const ReviewRecord: FC<Props> = ({ record, onChange, onCompoundChange, saveAll, statuses, showSave, initialValues }) => {
  const [errors, setErrors] = useState<{ comment?: string; reference?: string }>({});
  const [codeNotes, setCodeNotes] = useState([]);

  useEffect(() => {
    const load = async () => {
      const result = await getReferenceCodes(DEFAULT_PARAMS_ALL);
      const _codeNotes = result?.data?.results.filter((r: any) => {
        const reviewTypeId = Number(initialValues?.reviewTypeId);
        const ndx = r.tradeIds.indexOf(reviewTypeId);
        return ndx > -1;
      });
      setCodeNotes(_codeNotes);
    };
    load();
  }, []);

  const save = () => {
    setErrors({});
    if (!record.comment) {
      setErrors({ comment: 'empty' });
      return;
    }
    if (!record.reference) {
      setErrors({ reference: 'empty' });
      return;
    }
    saveAll();
  };

  const getStatusValue = stt => {
    if (typeof stt === 'object' && stt !== null) {
      return stt.value;
    }
    if (Number.isNaN(Number(stt))) {
      return stt;
    }
    return Number(stt);
  };

  return (
    <RecordCard>
      <IndexBadge>{record.index}</IndexBadge>

      <FieldsGrid>
        <FieldRefCode>
          <FieldLabelMobile>
            Reference Code <Required>*</Required>
          </FieldLabelMobile>
          <ReferenceDropdown
            codeNotes={codeNotes}
            errors={errors}
            record={record}
            onChange={e => onChange(e, record.index)}
            onCompoundChange={onCompoundChange}
          />
        </FieldRefCode>

        <FieldStatus>
          <FieldLabelMobile>
            Status <Required>*</Required>
          </FieldLabelMobile>
          <SelectStatus value={getStatusValue(record.status)} name="status" onChange={e => onChange(e, record.index)}>
            {statuses.map((status, index) => (
              <option key={index} value={status.value}>
                {status.label}
              </option>
            ))}
          </SelectStatus>
        </FieldStatus>

        <FieldComment>
          <FieldLabelMobile>
            Reviewer Comment <Required>*</Required>
          </FieldLabelMobile>
          <Textarea
            $error={errors.comment !== undefined}
            name="comment"
            placeholder="Describe what needs to be addressed…"
            value={record.comment || ''}
            onChange={e => onChange(e, record.index)}
          />
        </FieldComment>
      </FieldsGrid>

      <SaveButton type="button" onClick={save} aria-label="Save comment">
        {showSave ? (
          <CheckCircleIcon style={{ fontSize: 20, color: 'var(--success-600, #2e8c54)' }} />
        ) : (
          <DoneIcon style={{ fontSize: 20 }} />
        )}
      </SaveButton>
    </RecordCard>
  );
};

export default ReviewRecord;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const RecordCard = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 36px;
  align-items: start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 540px) {
    grid-template-columns: 28px minmax(0, 1fr) 36px;
    padding: 12px;
    gap: 10px;
  }
`;

const IndexBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  font-size: 0.8125rem;
  font-weight: 700;
  margin-top: 2px;

  @media only screen and (max-width: 540px) {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px;
  gap: 10px 12px;
  min-width: 0;

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FieldRefCode = styled(Field)``;

const FieldStatus = styled(Field)`
  @media only screen and (max-width: 540px) {
    grid-column: 1 / -1;
  }
`;

const FieldComment = styled(Field)`
  grid-column: 1 / -1;
`;

const FieldLabelMobile = styled.div`
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Required = styled.span`
  color: var(--danger-500, #d44b3f);
  margin-left: 2px;
`;

const Textarea = styled.textarea<{ $error: boolean }>`
  width: 100%;
  min-height: 64px;
  padding: 8px 12px;
  border: 1px solid ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--mainText);
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--neutral-400)')};
  }

  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--primary-600)')};
    box-shadow: var(--shadow-focus);
  }
`;

const SelectStatus = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 32px 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--neutral-0);
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--mainText);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  &::-ms-expand {
    display: none;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-top: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-600);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  padding: 0;

  &:hover {
    background: var(--primary-50);
    color: var(--primary-700);
    border-color: var(--primary-100, #cfe6f3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
